/**
 * `createOrder` — the effect whose loss stranded the withdraw.
 *
 * Today this is a bare `socket.emit` with no ack, no retry and no persistence
 * ([userDetails.js:1315]). Here the same emit is made durable: it is retried
 * until the server acknowledges it, and it is skipped entirely if the order
 * already exists.
 *
 * The emit itself is byte-identical to the existing one apart from an added
 * `effectKey`, which older servers simply ignore — so this is safe to ship
 * before the server-side gate lands.
 */

import axios from "axios";
import { registerEffect } from "../outbox";
import api from "../../config/api";

export const CREATE_ORDER_EFFECT = "createOrder";

const ACK_TIMEOUT_MS = 20000;

/**
 * Did the order already get created?
 *
 * `createFiatTransaction` sets `user.isOrderProcessing = true` as its last act,
 * and refuses to create a second open order while it is set. So a truthy value
 * here means our emit (or an earlier attempt) already landed. One existing
 * endpoint, no new API surface.
 */
async function verify({ ctx }) {
  const userId = ctx?.user?._id || ctx?.user?.id;
  if (!userId || !ctx?.token) return false;

  const response = await axios.get(api.userDetails.userPersonalDetails(userId), {
    headers: { Authorization: `Bearer ${ctx.token}` },
  });
  // `GET /user/:id` answers `{ data: { user, userWallet } }` — the flag is one
  // level deeper than this used to read, so the check was `undefined === true`
  // for every user and the backstop sent on every withdraw. Every other client
  // caller reads `?.data?.data?.user` (redux/actions/auth.js).
  return response?.data?.data?.user?.isOrderProcessing === true;
}

function send({ payload, effectKey, ctx }) {
  const socket = ctx?.socket;
  if (!socket || typeof socket.emit !== "function") {
    return Promise.reject(new Error("socket unavailable"));
  }
  if (!ctx?.token) return Promise.reject(new Error("no auth token"));

  return new Promise((resolve, reject) => {
    let settled = false;

    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      socket.off("orderCreatedForUser", onCreated);
      socket.off("effectAck", onAck);
      fn(value);
    };

    /**
     * Preferred acknowledgement: precise, addressed to this exact effect, and
     * able to say "duplicate" rather than making us infer it.
     */
    const onAck = (ack) => {
      if (!ack || ack.effectKey !== effectKey) return;
      if (ack.applied === "failed") {
        finish(reject, new Error(ack.error || "server reported effect failure"));
        return;
      }
      finish(resolve, ack.applied === "duplicate" ? "duplicate" : "applied");
    };

    /**
     * Fallback for a server that predates the ack. `orderCreatedForUser` is
     * broadcast rather than addressed, so it is a weaker signal — but combined
     * with the grace period and `verify()` it is still safe, and it means the
     * client can ship before the backend does.
     */
    const onCreated = (response) => {
      if (response === false) finish(reject, new Error("server rejected createOrder"));
      else finish(resolve, "applied");
    };

    const timer = setTimeout(
      () => finish(reject, new Error("timed out waiting for createOrder ack")),
      ACK_TIMEOUT_MS
    );

    socket.on("effectAck", onAck);
    socket.on("orderCreatedForUser", onCreated);

    try {
      socket.emit(CREATE_ORDER_EFFECT, { ...payload, token: ctx.token, effectKey });
    } catch (err) {
      finish(reject, err);
    }
  });
}

/**
 * `CreateOrder.jsx` still dispatches `createOrder` directly on the happy path
 * (the nonce-driven trigger that is its only working one — see the migration
 * note in the plan). The outbox is therefore a BACKSTOP during this phase: the
 * grace period lets the direct emit land and set `isOrderProcessing`, so
 * `verify()` settles the row without sending a second time.
 *
 * When the direct emit never happened — reload, dead socket, closed tab — the
 * grace period simply elapses and the outbox delivers.
 *
 * Once the direct dispatch is removed (commit (b) of the migration) this can
 * drop to 0.
 */
const GRACE_PERIOD_MS = 15000;

registerEffect({ type: CREATE_ORDER_EFFECT, send, verify, gracePeriodMs: GRACE_PERIOD_MS });

export const __testing = { verify, send, ACK_TIMEOUT_MS, GRACE_PERIOD_MS };
